import { Suspense, lazy } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell.jsx';
import { ProtectedRoute, PublicOnlyRoute, RoleGate } from '@/routes/guards.jsx';
import { Button } from '@/components/ui/button.jsx';
import { LoginPage } from '@/features/auth/LoginPage.jsx';

/**
 * The router.
 *
 * Screens are lazy-loaded, and the split is drawn where it pays: Recharts is
 * roughly a third of the bundle and is needed by the dashboard alone, so an
 * operator who opens the panel to check a withdrawal never downloads it.
 * `LoginPage` is the exception — it is eagerly imported because it is the first
 * thing an unauthenticated visitor sees, and a spinner before the login form is
 * a spinner before anything useful has happened.
 */

const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage.jsx'));
const UsersPage = lazy(() => import('@/features/users/UsersPage.jsx'));
const UserDetailPage = lazy(() => import('@/features/users/UserDetailPage.jsx'));
const StakesPage = lazy(() => import('@/features/stakes/StakesPage.jsx'));
const StakeDetailPage = lazy(() => import('@/features/stakes/StakeDetailPage.jsx'));
const WithdrawalsPage = lazy(() => import('@/features/withdrawals/WithdrawalsPage.jsx'));
const WithdrawalDetailPage = lazy(() => import('@/features/withdrawals/WithdrawalDetailPage.jsx'));
const IncomePage = lazy(() => import('@/features/income/IncomePage.jsx'));
const OffersPage = lazy(() => import('@/features/offers/OffersPage.jsx'));
const OfferAchievementDetailPage = lazy(
  () => import('@/features/offers/OfferAchievementDetailPage.jsx'),
);
const RegistrationRewardsPage = lazy(
  () => import('@/features/registration-rewards/RegistrationRewardsPage.jsx'),
);
const RegistrationRewardDetailPage = lazy(
  () => import('@/features/registration-rewards/RegistrationRewardDetailPage.jsx'),
);
const PackagesPage = lazy(() => import('@/features/packages/PackagesPage.jsx'));
const RanksPage = lazy(() => import('@/features/ranks/RanksPage.jsx'));
const UplineLevelsPage = lazy(() => import('@/features/upline/UplineLevelsPage.jsx'));
const AuditLogPage = lazy(() => import('@/features/audit/AuditLogPage.jsx'));
const SystemPage = lazy(() => import('@/features/system/SystemPage.jsx'));
const ControlsPage = lazy(() => import('@/features/controls/ControlsPage.jsx'));
const ChangePasswordPage = lazy(() => import('@/features/auth/ChangePasswordPage.jsx'));

/** Shown while a route chunk loads. Brief, and inside the shell already. */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24" role="status">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <p className="text-4xl font-semibold tracking-tight text-muted-foreground">404</p>
      <h1 className="mt-2 text-lg font-semibold">Page not found</h1>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        This address does not match any screen in the panel.
      </p>
      <Button asChild variant="outline" size="sm" className="mt-5">
        <Link to="/">Back to the dashboard</Link>
      </Button>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route element={<PublicOnlyRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route
          element={
            <Suspense fallback={<RouteFallback />}>
              <AppShell />
            </Suspense>
          }
        >
          <Route index element={<DashboardPage />} />

          <Route path="users" element={<UsersPage />} />
          <Route path="users/:id" element={<UserDetailPage />} />

          <Route path="stakes" element={<StakesPage />} />
          <Route path="stakes/:id" element={<StakeDetailPage />} />

          <Route path="withdrawals" element={<WithdrawalsPage />} />
          <Route path="withdrawals/:id" element={<WithdrawalDetailPage />} />

          <Route path="income" element={<IncomePage />} />

          <Route path="offers" element={<OffersPage />} />
          <Route path="offers/:id" element={<OfferAchievementDetailPage />} />

          <Route path="registration-rewards" element={<RegistrationRewardsPage />} />
          <Route path="registration-rewards/:id" element={<RegistrationRewardDetailPage />} />

          <Route path="packages" element={<PackagesPage />} />
          <Route path="ranks" element={<RanksPage />} />
          <Route path="upline-levels" element={<UplineLevelsPage />} />

          {/*
           * Guarded in the client as a courtesy — the API answers 403 whatever
           * this renders. See routes/guards.jsx.
           */}
          <Route
            path="audit-log"
            element={
              <RoleGate>
                <AuditLogPage />
              </RoleGate>
            }
          />

          <Route path="system" element={<SystemPage />} />
          <Route path="controls" element={<ControlsPage />} />
          <Route path="account/password" element={<ChangePasswordPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
